<template>
  <v-card id="acceptance-photos" class="pa-4 mb-4">
    <div class="text-subtitle-1 font-weight-medium">
      {{ $t("acceptance.photos.title") }}
    </div>
    <div class="text-caption text-grey-darken-1 mb-3">
      {{ $t("acceptance.photos.limit_note", { max }) }}
    </div>

    <!-- Each slot shows its latest photo; older ones stay under "+N". -->
    <div class="acceptance-photo-slots">
      <div v-for="slot in slots" :key="slot.kind" class="acceptance-photo-slot">
        <div class="text-caption text-grey-darken-1 mb-1">
          {{ $t(`acceptance.photos.kinds.${slot.kind}`) }}
          <span v-if="slot.count">· {{ slot.count }}/{{ max }}</span>
        </div>
        <div
          v-if="slot.latest"
          class="acceptance-photo-box acceptance-photo-thumb"
          role="button"
          tabindex="0"
          :title="nameOf(slot.latest)"
          @click="$emit('open', slot.latest)"
          @keydown.enter="$emit('open', slot.latest)"
        >
          <img
            v-if="thumbs[slot.latest]"
            :src="thumbs[slot.latest]"
            :alt="$t(`acceptance.photos.kinds.${slot.kind}`)"
          />
          <v-icon v-else-if="failed[slot.latest]" color="grey">
            mdi-image-broken-variant
          </v-icon>
          <v-progress-circular v-else indeterminate size="24" color="grey" />
          <span class="acceptance-photo-name text-caption">
            {{ nameOf(slot.latest) }}
          </span>
          <v-btn
            v-if="canRemove(slot.latest)"
            class="acceptance-photo-remove"
            icon="mdi-delete-outline"
            size="x-small"
            variant="flat"
            :disabled="disabled"
            :title="$t('acceptance.files.remove')"
            :aria-label="$t('acceptance.files.remove')"
            @click.stop="$emit('remove', slot.latest)"
          />
          <v-menu v-if="slot.others.length" location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-chip
                v-bind="menuProps"
                size="x-small"
                class="acceptance-photo-more"
                :title="$t('acceptance.photos.others')"
                @click.stop
              >
                +{{ slot.others.length }}
              </v-chip>
            </template>
            <v-list density="compact">
              <v-list-subheader>
                {{ $t("acceptance.photos.others") }}
              </v-list-subheader>
              <v-list-item
                v-for="fileId in slot.others"
                :key="fileId"
                :title="nameOf(fileId)"
                :subtitle="dateOf(fileId)"
                @click="$emit('open', fileId)"
              >
                <template v-if="canRemove(fileId)" #append>
                  <v-btn
                    icon="mdi-delete-outline"
                    size="small"
                    variant="text"
                    :disabled="disabled"
                    :title="$t('acceptance.files.remove')"
                    :aria-label="$t('acceptance.files.remove')"
                    @click.stop="$emit('remove', fileId)"
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div v-else class="acceptance-photo-box acceptance-photo-empty">
          <span class="text-body-2">{{ $t("acceptance.photos.empty") }}</span>
        </div>
        <v-btn
          class="mt-2"
          size="small"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-camera"
          :loading="uploading === slot.kind"
          :disabled="disabled || slot.full"
          :title="
            slot.full
              ? $t('acceptance.files.errors.file_limit_reached', { max })
              : undefined
          "
          @click="pick(slot.kind)"
        >
          {{ $t("acceptance.photos.upload") }}
        </v-btn>
      </div>
    </div>

    <input
      ref="picker"
      type="file"
      class="d-none"
      :accept="accept"
      @change="picked"
    />
  </v-card>
</template>

<script>
import moment from "moment";
import {
  IMAGE_TYPES,
  MAX_FILES_PER_TARGET,
} from "@/components/Acceptance/dossierFiles.mjs";

const KINDS = ["vin", "plate", "condition"];

export default {
  name: "AcceptancePhotosCard",
  props: {
    // dossier.photos: { fileId, kind } in upload order
    photos: { type: Array, default: () => [] },
    // view.files: metadata (originalName, uploadedAt) by id
    files: { type: Array, default: () => [] },
    // createBlobCache() from the page, which revokes the URLs on unmount
    blobs: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    // photo kind being uploaded, or null
    uploading: { type: String, default: null },
    // ids the current user may remove (cosmetic; the API decides)
    removableIds: { type: Array, default: () => [] },
  },
  emits: ["upload", "open", "remove"],
  data: () => ({
    thumbs: {},
    failed: {},
    pickedKind: null,
  }),
  computed: {
    accept() {
      return IMAGE_TYPES.join(",");
    },
    max() {
      return MAX_FILES_PER_TARGET;
    },
    filesById() {
      return Object.fromEntries(this.files.map((file) => [file.id, file]));
    },
    slots() {
      return KINDS.map((kind) => {
        const ids = this.photos
          .filter((photo) => photo.kind === kind)
          .map((photo) => photo.fileId);
        return {
          kind,
          latest: ids.length ? ids[ids.length - 1] : null,
          others: ids.slice(0, -1).reverse(),
          count: ids.length,
          full: ids.length >= MAX_FILES_PER_TARGET,
        };
      });
    },
    latestIds() {
      return this.slots.map((slot) => slot.latest).filter(Boolean);
    },
  },
  watch: {
    latestIds: {
      immediate: true,
      handler(ids) {
        ids.forEach((fileId) => this.loadThumb(fileId));
      },
    },
  },
  methods: {
    loadThumb(fileId) {
      if (this.thumbs[fileId]) return;
      this.blobs
        .get(fileId)
        .then((url) => {
          this.thumbs = { ...this.thumbs, [fileId]: url };
        })
        .catch((err) => {
          if (err && err.code === "revoked") return;
          this.failed = { ...this.failed, [fileId]: true };
        });
    },
    canRemove(fileId) {
      return this.removableIds.includes(fileId);
    },
    nameOf(fileId) {
      const file = this.filesById[fileId];
      return (file && file.originalName) || "—";
    },
    dateOf(fileId) {
      const file = this.filesById[fileId];
      return file && file.uploadedAt
        ? moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")
        : "";
    },
    pick(kind) {
      this.pickedKind = kind;
      this.$refs.picker.value = "";
      this.$refs.picker.click();
    },
    picked(event) {
      const file = event.target.files && event.target.files[0];
      if (file && this.pickedKind) this.$emit("upload", this.pickedKind, file);
      this.pickedKind = null;
    },
  },
};
</script>

<style scoped>
.acceptance-photo-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.acceptance-photo-slot {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 160px;
}
.acceptance-photo-box {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 112px;
  border-radius: 4px;
  overflow: hidden;
}
.acceptance-photo-thumb {
  cursor: pointer;
  background: rgba(0, 0, 0, 0.06);
}
.acceptance-photo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.acceptance-photo-name {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 2px 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
}
.acceptance-photo-more {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.9);
}
.acceptance-photo-remove {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(255, 255, 255, 0.9);
}
.acceptance-photo-empty {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  color: rgba(0, 0, 0, 0.6);
}
</style>
